import React, { useEffect } from 'react';

import { openMeetingJoinInfo } from 'flow/actions/meeting-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectActiveMeetingIsInstantMeeting,
    selectActiveMeetingRightPanelKind,
} from 'flow/selectors/meeting-selectors';
import MeetingChatPanel from './MeetingChatPanel';
import MeetingHostControlsPanel from './MeetingHostControlsPanel';
import MeetingJoinInfoPanel from './MeetingJoinInfoPanel';
import MeetingParticipantsPanel from './MeetingParticipantsPanel';
import MeetingSidePanel from './MeetingSidePanel';

interface Props {
    /**
     * Takeover: the panel fills the meeting area instead of the fixed side
     * width — computed once here (rather than per-content-panel) since every
     * panel kind shares the same decision.
     */
    fill: boolean;
}

/**
 * The single right-hand panel slot: chat, participants, join-info and host
 * controls are mutually exclusive (enforced in meeting-actions.js — every
 * open/toggle thunk for one closes the other 3), so this renders exactly one
 * MeetingSidePanel and swaps its *content* based on which is active, instead
 * of mounting 4 independent sliding panels. That distinction matters for the
 * open/close animation: with 4 independent panels, switching from one to
 * another animated both a slide-out and a slide-in at once (visibly wrong —
 * see the Figma prototype video, where switching panels is an instant content
 * swap in place). With one shared panel, only a true open (nothing was
 * active) or close (nothing becomes active) animates; switching which panel
 * is active is instantaneous.
 */
const MeetingRightPanel = ({ fill }: Props) => {
    const dispatch = useAppDispatch();
    const panelKind = useAppSelector(selectActiveMeetingRightPanelKind);
    const isInstantMeeting = useAppSelector(
        selectActiveMeetingIsInstantMeeting,
    );

    useEffect(() => {
        if (isInstantMeeting) {
            dispatch(openMeetingJoinInfo());
        }
    }, [isInstantMeeting, dispatch]);

    return (
        <MeetingSidePanel
            open={panelKind !== null}
            fill={fill}
            className="flex-col bg-black py-3 pe-3"
        >
            {panelKind === 'chat' && <MeetingChatPanel />}
            {panelKind === 'participants' && <MeetingParticipantsPanel />}
            {panelKind === 'joinInfo' && <MeetingJoinInfoPanel />}
            {panelKind === 'hostControls' && <MeetingHostControlsPanel />}
        </MeetingSidePanel>
    );
};

export default MeetingRightPanel;
